import React, { useEffect, useState } from "react";
import {
  getCareerCounselingServices,
  updateCareerCounselingService,
} from "../../../services/user.services";
import { useDispatch } from "react-redux";
import {
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

function CareerCounselingTable({
  services,
  setServices,
  saveCareerCouncilingServices,
  deleteService,
  coreServiceId,
}) {
  const [editId, setEditId] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleEdit = (service) => {
    setEditId(service.id);
    setEditableData({
      id: service.id,
      service_name: service.service_name || "",
      price: service.price || "",
    });
  };

  const cancelEdit = (id) => {
    if (services.find((service) => service.id === id)?.isNew) {
      setServices((prev) => prev.filter((service) => service.id !== id));
    }
    setEditId(null);
    setEditableData({});
  };

  const dispatch = useDispatch();

  // Handle Input Change
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditableData({ ...editableData, [name]: value });
  };

  const saveEdit = async () => {
    try {
      const updatedData = {
        id: editId,
        service_name: editableData.service_name,
        price: editableData.price,
        coreservices_id: coreServiceId,
      };
      const doesExist = services.find(
        (service) => service.id === updatedData.id
      );
      if (!doesExist) {
        await saveCareerCouncilingServices({
          id: editId,
          service_name: editableData.service_name,
          price: editableData.price,
          coreservices_id: coreServiceId,
        });

        setServices((prev) =>
          prev.map((item) =>
            item.id === editId
              ? { ...item, ...editableData, isNew: false }
              : item
          )
        );
      } else {
        await updateCareerCounselingService(editId, {
          id: editId,
          service_name: editableData.service_name,
          price: editableData.price,
          coreservices_id: coreServiceId,
        });

        setServices((prev) =>
          prev.map((item) =>
            item.id === editId ? { ...item, ...updatedData } : item
          )
        );
      }

      setEditId(null);
      setEditableData({});
    } catch (error) {
      console.error("Error saving the service:", error.message);
    }
  };

  // Delete Row
  const deleteRow = async (id) => {
    try {
      await deleteService(id);
      setServices(services.filter((service) => service.id !== id));
    } catch (error) {
      console.error("Error deleting the service:", error);
    }
  };

  const toggleSelect = (id) => {
    setServices(
      services.map((service) =>
        service.id === id
          ? { ...service, selected: !service.selected }
          : service
      )
    );
  };

  // Toggle expanded state for mobile cards
  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // Render input field
  const renderInput = (name, value, placeholder, type = "text") => (
    <input
      type={type}
      name={name}
      value={value}
      onChange={handleEditChange}
      placeholder={placeholder}
      className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
    />
  );

  // Mobile Card Component
  const MobileCard = ({ service }) => {
    const isEditing = editId === service.id || service.isNew;
    const isExpanded = expandedRows.has(service.id);

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-3">
        {/* Card Header */}
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1">
              <input
                type="checkbox"
                className="cursor-pointer flex-shrink-0"
                checked={service.selected}
                onChange={() => toggleSelect(service.id)}
              />
              <div className="flex-1 min-w-0">
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Name
                  </label>
                  {isEditing ? (
                    renderInput(
                      "service_name",
                      editableData.service_name ?? service.service_name,
                      "Service Name"
                    )
                  ) : (
                    <h3 className="font-medium text-gray-900 truncate">
                      {service.service_name || "Untitled Service"}
                    </h3>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price
                  </label>
                  {isEditing ? (
                    renderInput(
                      "price",
                      editableData.price ?? service.price,
                      "Price"
                    )
                  ) : (
                    <p className="text-blue-600 font-medium">
                      {service.price || "N/A"}
                    </p>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-2">ID: {service.id}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
              {/* Action Buttons */}
              {isEditing ? (
                <>
                  <button
                    onClick={saveEdit}
                    className="text-green-600 hover:text-green-800 p-2"
                    title="Save"
                  >
                    <FaSave />
                  </button>
                  <button
                    onClick={() => cancelEdit(service.id)}
                    className="text-gray-600 hover:text-gray-800 p-2"
                    title="Cancel"
                  >
                    <FaTimes />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => toggleEdit(service)}
                    className="text-blue-600 hover:text-blue-800 p-2"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => deleteRow(service.id)}
                    className="text-red-600 hover:text-red-800 p-2"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Mobile View */}
      <div className="block md:hidden">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Career Counseling Services
            </h3>
            <p className="text-sm text-gray-500">{services.length} items</p>
          </div>

          {services.length > 0 ? (
            services.map((service) => (
              <MobileCard key={service.id} service={service} />
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">
                No career counseling services available
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <div className="container mx-auto p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-4 text-center">
                    <input type="checkbox" className="cursor-pointer" />
                  </th>
                  <th className="py-3 px-4 text-left">SL</th>
                  <th className="py-3 px-4 text-left">Name of the Services</th>
                  <th className="py-3 px-4 text-left">Price</th>
                  <th className="py-3 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.length > 0 ? (
                  services.map((service) => (
                    <tr
                      key={service.id}
                      className="border-b border-gray-300 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4 text-center">
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={service.selected}
                          onChange={() => toggleSelect(service.id)}
                        />
                      </td>
                      <td className="py-3 px-4 text-center">{service.id}</td>
                      <td className="py-3 px-4">
                        {editId === service.id || service.isNew ? (
                          <input
                            type="text"
                            name="service_name"
                            value={
                              editableData.service_name ?? service.service_name
                            }
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Service Name"
                          />
                        ) : (
                          service.service_name
                        )}
                      </td>
                      <td className="py-3 px-4 text-blue-600">
                        {editId === service.id || service.isNew ? (
                          <input
                            type="text"
                            name="price"
                            value={editableData.price ?? service.price}
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Price"
                          />
                        ) : (
                          service.price
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          {editId === service.id || service.isNew ? (
                            <>
                              <button
                                onClick={saveEdit}
                                className="text-green-600 hover:text-green-800 p-1"
                                title="Save"
                              >
                                <FaSave />
                              </button>
                              <button
                                onClick={() => cancelEdit(service.id)}
                                className="text-gray-600 hover:text-gray-800 p-1"
                                title="Cancel"
                              >
                                <FaTimes />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => toggleEdit(service)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => deleteRow(service.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-8 text-center text-gray-500">
                      No career counseling services available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CareerCounselingTable;
